import React from 'react';
import { useTranslation } from 'react-i18next';
import { UserOutlined } from '@ant-design/icons';

import {
  InfoColumn,
  StyledItem,
  StyledLabel,
  StyledValue,
  StyledWrapper,
  StyledInfoColumnTitle,
} from './PartnerInfoStyles';
import { IPartnerProfile } from '@/models/partners.model';
import { FileUpload } from '@/components';
import { OwnershipTypes } from '../../helpers/types';
import { CompanyRow } from '../CompanyDataFields/CompanyDataFieldsStyles';

interface IPartnerInfo {
  data: IPartnerProfile | null;
}

const displayValue = (value?: string | null) => value?.trim() || '-';

const PartnerInfo = ({ data }: IPartnerInfo) => {
  const { t } = useTranslation(['partners', 'common']);

  const ownership = data?.ownership_form;
  const vatPayer = data?.is_vat_payer;
  const hasNoAccountant = data?.has_no_accountant;

  const missing = t('partners_not_specified');

  const bankAccounts = data?.bank_accounts ?? [];
  const bankSections = bankAccounts.length ? bankAccounts : [null];

  return (
    <StyledWrapper>
      <InfoColumn>
        <StyledInfoColumnTitle>{t('partners_company_data')}</StyledInfoColumnTitle>

        <StyledItem>
          <StyledLabel>{t('partners_company')}</StyledLabel>
          <StyledValue>{displayValue(data?.title)}</StyledValue>
        </StyledItem>

        <CompanyRow>
          <StyledItem>
            <StyledLabel>{t('partners_ownership')}</StyledLabel>
            <StyledValue>
              {ownership
                ? t(ownership === OwnershipTypes.FOP ? 'partners_company_fop' : 'partners_legal_entity')
                : missing}
            </StyledValue>
          </StyledItem>

          <StyledItem>
            <StyledLabel>{t(ownership === OwnershipTypes.FOP ? 'partners_drfo' : 'partners_tax_code')}</StyledLabel>
            <StyledValue>
              {ownership === OwnershipTypes.FOP
                ? displayValue(data?.drfo)
                : ownership === OwnershipTypes.LEGAL_ENTITY
                ? displayValue(data?.edrpou)
                : missing}
            </StyledValue>
          </StyledItem>
        </CompanyRow>

        <CompanyRow>
          <StyledItem>
            <StyledLabel>{t('partners_vat_payer')}</StyledLabel>
            <StyledValue>{vatPayer == null ? missing : t(vatPayer ? 'partners_yes' : 'partners_no')}</StyledValue>
          </StyledItem>

          <StyledItem>
            <StyledLabel>{t('partners_ipn')}</StyledLabel>
            <StyledValue>
              {vatPayer === false ? t('partners_not_vat_payer') : vatPayer === true ? displayValue(data?.ipn) : missing}
            </StyledValue>
          </StyledItem>
        </CompanyRow>

        <CompanyRow>
          <StyledItem>
            <StyledLabel>{t('partners_company_payment_target')}</StyledLabel>
            <StyledValue>{data?.legal_type ? t(`partners_company_${data.legal_type}`) : missing}</StyledValue>
          </StyledItem>

          <StyledItem>
            <StyledLabel>{t('partners_tax_type')}</StyledLabel>
            <StyledValue>{data?.tax_type ? t(data.tax_type, { ns: 'common' }) : missing}</StyledValue>
          </StyledItem>
        </CompanyRow>

        <StyledItem>
          <StyledLabel>{t('partners_legal_address')}</StyledLabel>
          <StyledValue>{displayValue(data?.legal_address)}</StyledValue>
        </StyledItem>

        <StyledItem>
          <StyledLabel>{t('partners_physical_address')}</StyledLabel>
          <StyledValue>{displayValue(data?.physical_address)}</StyledValue>
        </StyledItem>

        {bankSections.map((account, index) => (
          <React.Fragment key={account?.id ?? `empty-${index}`}>
            <StyledInfoColumnTitle>
              {t('partners_bank_information')}
              {index > 0 ? ` №${index + 1}` : ''}
            </StyledInfoColumnTitle>

            <CompanyRow>
              <StyledItem>
                <StyledLabel>{t('partners_bank_name')}</StyledLabel>
                <StyledValue>{displayValue(account?.bank_name)}</StyledValue>
              </StyledItem>

              <StyledItem>
                <StyledLabel>{t('partners_mfo')}</StyledLabel>
                <StyledValue>{displayValue(account?.mfo)}</StyledValue>
              </StyledItem>
            </CompanyRow>

            <StyledItem>
              <StyledLabel>{t('partners_iban')}</StyledLabel>
              <StyledValue>{displayValue(account?.iban)}</StyledValue>
            </StyledItem>
          </React.Fragment>
        ))}

        <StyledInfoColumnTitle>{t('partners_accountant_contacts')}</StyledInfoColumnTitle>

        {hasNoAccountant === true ? (
          <StyledItem>
            <StyledValue>{t('partners_no_accountant')}</StyledValue>
          </StyledItem>
        ) : hasNoAccountant === false ? (
          <>
            <StyledItem>
              <StyledLabel>{t('partners_accountant_first_name')}</StyledLabel>
              <StyledValue>{displayValue(data?.accountant_first_name)}</StyledValue>
            </StyledItem>

            <StyledItem>
              <StyledLabel>{t('partners_accountant_last_name')}</StyledLabel>
              <StyledValue>{displayValue(data?.accountant_last_name)}</StyledValue>
            </StyledItem>

            <StyledItem>
              <StyledLabel>{t('partners_accountant_middle_name')}</StyledLabel>
              <StyledValue>{displayValue(data?.accountant_middle_name)}</StyledValue>
            </StyledItem>

            <StyledItem>
              <StyledLabel>{t('partners_accountant_email')}</StyledLabel>
              <StyledValue>{displayValue(data?.accountant_email)}</StyledValue>
            </StyledItem>

            <StyledItem>
              <StyledLabel>{t('partners_accountant_phone')}</StyledLabel>
              <StyledValue>{displayValue(data?.accountant_phone)}</StyledValue>
            </StyledItem>
          </>
        ) : (
          <StyledItem>
            <StyledValue>{missing}</StyledValue>
          </StyledItem>
        )}
      </InfoColumn>

      <InfoColumn>
        <StyledInfoColumnTitle>{t('partners_authorized_person_data')}</StyledInfoColumnTitle>

        <StyledItem>
          <StyledLabel>{t('partners_contact_person_name')}</StyledLabel>
          <StyledValue>{displayValue(data?.first_name)}</StyledValue>
        </StyledItem>

        <StyledItem>
          <StyledLabel>{t('partners_contact_person_last_name')}</StyledLabel>
          <StyledValue>{displayValue(data?.last_name)}</StyledValue>
        </StyledItem>

        <StyledItem>
          <StyledLabel>{t('partners_contact_person_middle_name')}</StyledLabel>
          <StyledValue>{displayValue(data?.middle_name)}</StyledValue>
        </StyledItem>

        <StyledItem>
          <StyledLabel>{t('partners_email')}</StyledLabel>
          <StyledValue>{displayValue(data?.email)}</StyledValue>
        </StyledItem>

        <StyledItem>
          <StyledLabel>{t('partners_contact_person_phone')}</StyledLabel>
          <StyledValue>{displayValue(data?.phone)}</StyledValue>
        </StyledItem>

        <StyledItem>
          <StyledLabel>{t('partners_subscriber_full_name')}</StyledLabel>
          <StyledValue>{displayValue(data?.subscriber_full_name)}</StyledValue>
        </StyledItem>

        <StyledItem>
          <StyledLabel>{t('partners_document')}</StyledLabel>
          <StyledValue>{data?.document ? <FileUpload value={data.document} disabled /> : '-'}</StyledValue>
        </StyledItem>

        <StyledInfoColumnTitle>{t('partners_additional_info')}</StyledInfoColumnTitle>

        <StyledItem>
          <StyledLabel>{t('partners_category_manager_optional')}</StyledLabel>
          <StyledValue>
            {data?.category_manager ? (
              <>
                <UserOutlined /> {data.category_manager.full_name}
              </>
            ) : (
              '-'
            )}
          </StyledValue>
        </StyledItem>
      </InfoColumn>
    </StyledWrapper>
  );
};

export default PartnerInfo;
